import { type Ref } from 'vue'
import request from '@/api/request'
import {
  parseCareScheduleContextResponse,
  type ParsedCareScheduleContext,
} from '@/api/CareScheduleContext'
// import { postCareScheduleAvailability } from '@/api/CareScheduleAvailability'
// import { formatAvailabilityWeekTimeEnd, formatAvailabilityWeekTimeStart, startOfWeekSunday } from '../utils/scheduleCalendar'
import type { CareScheduleFocusPayload } from '../types'

export interface FetchScheduleContextResult {
  ctx: ParsedCareScheduleContext
  rawData?: Record<string, unknown>
  weekStartYmd: string
}

export interface UseCareScheduleDataSyncParams {
  focusPayload: Ref<CareScheduleFocusPayload | null>
  weekAnchor: Ref<Date>
  selectedInspectorId: Ref<string>
  selectedOrderId: Ref<string>
  inspectorAvailability: Ref<import('@/api/CareScheduleAvailability').ParsedCareScheduleAvailability | null>
  findOrder: (orderId: string) => import('@/api/orders').OrderCardDTO | undefined
  resolveContextTargetForFocus: (
    focus: CareScheduleFocusPayload,
  ) => Promise<{ target_type: 'order' | 'followup'; target_id: number } | null>
  applyParsedScheduleContext: (
    ctx: ParsedCareScheduleContext,
    focus: CareScheduleFocusPayload,
    opts?: {
      assignmentsOnly?: boolean
      rawData?: Record<string, unknown>
      skipDayStatusUpdate?: boolean
    },
  ) => void
  lineIndexForOrder: (orderId: string) => number
  isFollowUpScheduleFocusSession: (focus: CareScheduleFocusPayload) => boolean
  followUpScheduleLineIndex: (o: import('@/api/orders').OrderCardDTO) => number
  technicianIdForAvailabilityApi: (inspectorId: string) => number
  contextWeekStartYmd: () => string
}

export function useCareScheduleDataSync(params: UseCareScheduleDataSyncParams) {
  const {
    focusPayload,
    weekAnchor,
    selectedInspectorId,
    selectedOrderId,
    inspectorAvailability,
    findOrder,
    resolveContextTargetForFocus,
    applyParsedScheduleContext,
    lineIndexForOrder,
    isFollowUpScheduleFocusSession,
    followUpScheduleLineIndex,
    technicianIdForAvailabilityApi,
    contextWeekStartYmd,
  } = params

  let scheduleContextLoadSeq = 0
  let availabilityLoadSeq = 0
  let scheduleDataRefreshInFlight = false
  let scheduleDataRefreshQueued = false

  /** 仅拉取 context，不写页面状态（用于按日预取 week-date-card 颜色） */
  async function fetchScheduleContextByWeekStart(
    focus: CareScheduleFocusPayload,
    weekStartYmd: string,
  ): Promise<FetchScheduleContextResult | null> {
    const target = await resolveContextTargetForFocus(focus)
    if (!target) return null
    const ymd = weekStartYmd.trim()
    if (!ymd) return null
    try {
      const raw = await request.post<{ code: number; msg: string; data?: unknown }>(
        '/api/admin/care-schedule/context',
        {
          target_type: target.target_type,
          target_id: target.target_id,
          week_start: ymd,
        },
      )
      const ctx = parseCareScheduleContextResponse(raw)
      if (!ctx) return null
      const rawData =
        raw?.data != null && typeof raw.data === 'object'
          ? (raw.data as Record<string, unknown>)
          : undefined
      return { ctx, rawData, weekStartYmd: ymd }
    } catch {
      return null
    }
  }

  async function loadScheduleContext(
    focus: CareScheduleFocusPayload,
    opts?: {
      assignmentsOnly?: boolean
      weekStartYmd?: string
      skipDayStatusUpdate?: boolean
    },
  ): Promise<boolean> {
    const weekStart = opts?.weekStartYmd?.trim() || contextWeekStartYmd()
    const seq = ++scheduleContextLoadSeq
    const fetched = await fetchScheduleContextByWeekStart(focus, weekStart)
    if (seq !== scheduleContextLoadSeq) return false
    if (!fetched) return false
    applyParsedScheduleContext(fetched.ctx, focus, {
      assignmentsOnly: opts?.assignmentsOnly,
      rawData: fetched.rawData,
      skipDayStatusUpdate: opts?.skipDayStatusUpdate,
    })
    return true
  }

  async function loadInspectorAvailability(): Promise<void> {
    // 临时关闭：不请求 care-schedule/availability，UI 回退纯本地占用推算
    inspectorAvailability.value = null
    return

    // const inspectorId = selectedInspectorId.value
    // const orderId = selectedOrderId.value
    // const focus = focusPayload.value
    // if (!inspectorId || !orderId || !focus) {
    //   inspectorAvailability.value = null
    //   return
    // }

    // const technicianId = technicianIdForAvailabilityApi(inspectorId)
    // if (!technicianId) return

    // const o = findOrder(orderId) ?? focus.orderList?.find((x) => x.id === orderId)
    // let lineIndex = lineIndexForOrder(orderId)
    // if (focus.mode !== 'reschedule' && isFollowUpScheduleFocusSession(focus) && o) {
    //   lineIndex = followUpScheduleLineIndex(o)
    // }
    // const target = await resolveContextTargetForFocus({
    //   ...focus,
    //   orderId,
    //   lineIndex,
    // })
    // if (!target) return

    // const seq = ++availabilityLoadSeq
    // try {
    //   const av = await postCareScheduleAvailability({
    //     technician_id: technicianId,
    //     time_start: formatAvailabilityWeekTimeStart(weekAnchor.value),
    //     time_end: formatAvailabilityWeekTimeEnd(weekAnchor.value),
    //     target_type: target.target_type,
    //     target_id: target.target_id,
    //   })
    //   if (seq !== availabilityLoadSeq) return
    //   inspectorAvailability.value = av
    // } catch {
    //   if (seq === availabilityLoadSeq) inspectorAvailability.value = null
    // }
  }

  async function refreshSchedulePageData(opts?: {
    assignmentsOnly?: boolean
    skipContext?: boolean
    skipAvailability?: boolean
    skipDayStatusUpdate?: boolean
  }): Promise<void> {
    const focus = focusPayload.value
    if (!focus?.orderId) return
    if (scheduleDataRefreshInFlight) {
      scheduleDataRefreshQueued = true
      return
    }
    scheduleDataRefreshInFlight = true
    try {
      if (!opts?.skipContext) {
        await loadScheduleContext(focus, {
          assignmentsOnly: opts?.assignmentsOnly ?? true,
          skipDayStatusUpdate: opts?.skipDayStatusUpdate,
        })
      }
      if (!opts?.skipAvailability) {
        await loadInspectorAvailability()
      }
    } finally {
      scheduleDataRefreshInFlight = false
      if (scheduleDataRefreshQueued) {
        scheduleDataRefreshQueued = false
        void refreshSchedulePageData(opts)
      }
    }
  }

  function refreshSchedulePageDataNow(opts?: {
    assignmentsOnly?: boolean
    skipContext?: boolean
    skipAvailability?: boolean
    skipDayStatusUpdate?: boolean
  }): void {
    void refreshSchedulePageData({
      assignmentsOnly: opts?.assignmentsOnly ?? true,
      skipContext: opts?.skipContext,
      skipAvailability: opts?.skipAvailability,
      skipDayStatusUpdate: opts?.skipDayStatusUpdate,
    })
  }

  return {
    loadScheduleContext,
    fetchScheduleContextByWeekStart,
    loadInspectorAvailability,
    refreshSchedulePageData,
    refreshSchedulePageDataNow,
  }
}
